import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * trail-making-a - vue React staircase + debrief (cyanotype Wave M2).
 *
 * Rendu canvas relatif (STAGE_SIZE x STAGE_SIZE). Cibles numerotees absolute
 * positioned. Click handler par cible -> adapter.handleTap(id). Compteur
 * erreurs live. Scientific caveat cite Reitan 1958 + Lezak.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DebriefShell, useGameShellFromContext } from '@labrain/game-shell';
import { staircaseConfig } from './params';
const STAGE_SIZE = 320;
const TARGET_SIZE = 36;
function Stage({ targets, visitedIds, nextExpectedId, enabled, onTap, ariaLabelTpl, }) {
    const visitedSet = useMemo(() => new Set(visitedIds), [visitedIds]);
    return (_jsx("div", { className: "relative border border-cyan-border bg-cyan-surface", style: { width: STAGE_SIZE, height: STAGE_SIZE }, children: targets.map((tg) => {
            const visited = visitedSet.has(tg.id);
            const isNext = enabled && tg.id === nextExpectedId;
            return (_jsx("button", { type: "button", onClick: () => enabled && onTap(tg.id), disabled: !enabled, "aria-label": ariaLabelTpl(tg.id), className: [
                    'absolute flex items-center justify-center font-display text-[14px] font-medium transition-colors',
                    visited
                        ? 'bg-cyan-core text-cyan-surface'
                        : isNext
                            ? 'border border-cyan-core bg-cyan-surface text-cyan-core'
                            : 'border border-cyan-border bg-cyan-surface text-cyan-text',
                    enabled ? 'cursor-pointer active:scale-95' : 'cursor-not-allowed opacity-70',
                ].join(' '), style: {
                    width: TARGET_SIZE,
                    height: TARGET_SIZE,
                    left: tg.x * STAGE_SIZE - TARGET_SIZE / 2,
                    top: tg.y * STAGE_SIZE - TARGET_SIZE / 2,
                }, children: tg.id }, `tg-${tg.id}`));
        }) }));
}
export function TMAStaircaseView({ adapter, onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const handleTap = useCallback((id) => adapter.handleTap(id), [adapter]);
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    const stim = adapterState.stim;
    const targets = stim?.targets ?? [];
    const enabled = adapterState.subPhase === 'awaiting_input';
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s5 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:labels.trial_count', {
                            current: Math.min(trialDisplay, staircaseConfig.maxTrials),
                            total: staircaseConfig.maxTrials,
                        }) }), _jsx("span", { className: "font-mono text-[11px]", children: t('trail_making_a.live.n_targets', { n: stim?.nTargets ?? 0 }) }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: t('common:actions.stop') })] }), _jsxs("div", { className: "flex h-[28px] items-center justify-center text-[14px]", children: [adapterState.subPhase === 'fixation' && (_jsx("span", { className: "text-cyan-text-secondary", children: "\u2026" })), adapterState.subPhase === 'presenting' && (_jsx("span", { className: "font-display text-cyan-network", children: t('trail_making_a.live.get_ready') })), adapterState.subPhase === 'awaiting_input' && (_jsx("span", { className: "text-cyan-text-secondary", children: t('trail_making_a.live.tap_next', { id: adapterState.nextExpectedId }) })), adapterState.subPhase === 'feedback' && (_jsx("span", { className: [
                            'font-display text-[16px] font-medium',
                            adapterState.feedback === 'correct'
                                ? 'text-cyan-core'
                                : 'text-cyan-media',
                        ].join(' '), children: adapterState.feedback === 'correct'
                            ? t('trail_making_a.feedback.correct')
                            : t('trail_making_a.feedback.has_errors') }))] }), _jsx(Stage, { targets: targets, visitedIds: adapterState.visitedIds, nextExpectedId: adapterState.nextExpectedId, enabled: enabled, onTap: handleTap, ariaLabelTpl: (id) => t('trail_making_a.target_aria', { id }) }), _jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono text-[11px]", children: t('trail_making_a.live.progress', {
                            done: adapterState.visitedIds.length,
                            total: stim?.nTargets ?? 0,
                        }) }), _jsx("span", { className: "font-mono text-[11px]", children: t('trail_making_a.live.errors', { n: adapterState.errors }) })] }), _jsx("div", { className: "text-[11px] text-cyan-text-secondary", children: t('trail_making_a.footer_hint') })] }));
}
function recommendKey(medianMs) {
    if (medianMs === null)
        return 'trail_making_a.debrief.recommend_keep';
    if (medianMs <= 15000)
        return 'trail_making_a.debrief.recommend_excellent';
    if (medianMs <= 30000)
        return 'trail_making_a.debrief.recommend_good';
    return 'trail_making_a.debrief.recommend_keep';
}
export function TMADebriefView({ onAbort: _onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const trialsCompleted = staircase.trialIdx;
    const trialsCorrect = staircase.trialLog.filter((tt) => tt.response.correct).length;
    const accuracy = trialsCompleted === 0
        ? 0
        : Math.round((trialsCorrect / trialsCompleted) * 100);
    const medianMs = useMemo(() => {
        const times = staircase.trialLog
            .map((tt) => tt.response.reactionTimeMs ?? 0)
            .filter((v) => v > 0)
            .sort((a, b) => a - b);
        if (times.length === 0)
            return null;
        const mid = Math.floor(times.length / 2);
        if (times.length % 2 === 1)
            return times[mid] ?? null;
        return Math.round(((times[mid - 1] ?? 0) + (times[mid] ?? 0)) / 2);
    }, [staircase.trialLog]);
    const fallback = !!staircase.fallback;
    const scoreLabel = t('trail_making_a.debrief.score_label');
    const scoreValue = medianMs === null ? '—' : (medianMs / 1000).toFixed(1);
    return (_jsx(DebriefShell, { eyebrow: t('common:eyebrow.session_done'), title: t('trail_making_a.debrief.title'), scoreLabel: scoreLabel, scoreValue: scoreValue, fallbackBadge: fallback, metrics: [
            { label: t('common:metrics.trials'), value: trialsCompleted },
            { label: t('common:metrics.correct_pct'), value: `${accuracy}%` },
        ], recommendation: t(recommendKey(medianMs)), scientificCaveat: t('trail_making_a.debrief.scientific_caveat'), ctaReplay: _jsx("button", { type: "button", onClick: () => window.location.reload(), className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.replay') }), ctaBack: _jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.choose_another') }) }));
}
//# sourceMappingURL=staircaseView.js.map